import React, { useState, useEffect } from 'react';
import { Select, Message, Input, Label } from 'semantic-ui-react';
import styled from 'styled-components';
import { StepComponentProps } from './OrganizationWizard';
import { Table, HeaderCell, TableBody, TableCell, TableHeader, TableRow } from 'common/components/Table';
import { organizationApi } from '../api/organizationApi';
import { useToasts } from 'common/toast';

const visibilityOptions = [
  { key: 'public', text: 'Public', value: 'public' },
  { key: 'private', text: 'Private', value: 'private' },
];

const memberPromptOptions = [
  { key: 'ask_if_not_found', text: 'Ask if not found', value: 'ask_if_not_found' },
  { key: 'never_ask', text: 'Never ask', value: 'never_ask' },
  { key: 'always_ask', text: 'Always ask', value: 'always_ask' },
];

const priorityOptions = [
  { key: 1, text: 'Primary', value: 1 },
  { key: 2, text: 'Custom', value: 2 },
  { key: 3, text: 'nuLogic', value: 3 },
];

const OrganizationWizardStep3Demographics: React.FC<StepComponentProps> = ({
  data,
  onDataChange,
  onNext,
  onBack,
  onCancel,
}) => {
  // Prefer any previously-configured categories; fall back to the raw selected categories
  const [configuredCategories, setConfiguredCategories] = useState(
    (data.configuredCategories && data.configuredCategories.length > 0)
      ? data.configuredCategories
      : data.selectedCategories
  );

  useEffect(() => {
    // Merge any newly selected categories into the configured list so edits survive
    // returning to this step after adding categories in Step 3. Also remove any
    // configured entries that are no longer selected.
    const selected = data.selectedCategories || [];
    const selectedIds = new Set(selected.map((s: any) => String(s.categoryId)));

    const configured = data.configuredCategories && data.configuredCategories.length > 0
      ? data.configuredCategories.slice()
      : [];

    // Start by filtering configured entries to only those still selected
    const filteredConfigured = configured.filter((c: any) => selectedIds.has(String(c.categoryId)));

    const configuredByCatId = new Set(filteredConfigured.map((c: any) => String(c.categoryId)));

    // Add any selected categories that aren't yet configured
    let merged = filteredConfigured.slice();
    selected.forEach((s: any) => {
      const key = String(s.categoryId);
      if (!configuredByCatId.has(key)) {
        merged.push({
          ...s,
          visibility: s.visibility || 'private',
          input: s.input || 'ask_if_not_found',
          question: s.question || '',
        });
        configuredByCatId.add(key);
      }
    });

    // If there are no configured entries yet, prefer selected directly (keeps previous behavior)
    if ((data.configuredCategories && data.configuredCategories.length > 0) || merged.length === 0) {
      // Only update state if merged differs from current to avoid unnecessary rerenders
      const sameLength = merged.length === configuredCategories.length;
      const sameItems = sameLength && merged.every((m: any, i: number) => String(m.categoryId) === String(configuredCategories[i]?.categoryId));
      if (!sameItems) {
        setConfiguredCategories(merged);
        onDataChange({ configuredCategories: merged });
      }
    } else {
      // No previously configured entries, use selected categories directly
      const selectedAsConfigured = selected.map((s: any) => ({
        ...s,
        visibility: s.visibility || 'private',
        input: s.input || 'ask_if_not_found',
        question: s.question || '',
      }));
      setConfiguredCategories(selectedAsConfigured);
      onDataChange({ configuredCategories: selectedAsConfigured });
    }
  }, [data.selectedCategories, data.configuredCategories]);

  const handleFieldChange = (index: number, field: string, value: any) => {
    const updated = [...configuredCategories];
    updated[index] = { ...updated[index], [field]: value };
    setConfiguredCategories(updated);

    // Persist the change immediately into wizard data so navigating away and back retains edits
    onDataChange({ configuredCategories: updated });
  };

  const [isSaving, setIsSaving] = useState(false);
  const { addToast } = useToasts();

  const { data: org } = organizationApi.useApiAdminV1OrganizationRetrieveQuery(
    { id: data.organizationId ?? -1 },
    { skip: !data.organizationId }
  );

  const [updateOrganization] = organizationApi.useApiAdminV1OrganizationPartialUpdateMutation();

  const handleContinue = async () => {
    onDataChange({ configuredCategories });

    // Persist visibility and input (prompt) changes to the organization's categories
    if (data.organizationId) {
      setIsSaving(true);
      try {
        const orgCategories = (org?.categories || []) as any[];

        // Build lookup by id (when available) and by name (case-insensitive)
        const configById = new Map<string | number, any>();
        const configByName = new Map<string, any>();
        configuredCategories.forEach((c: any) => {
          if (c.categoryId != null) configById.set(String(c.categoryId), c);
          configByName.set((c.name || '').toLowerCase(), c);
        });

        // Merge updates into existing org categories
        const merged = orgCategories.map(cat => {
          const byId = configById.get(String(cat.id));
          const byName = configByName.get((cat.name || '').toLowerCase());
          const match = byId || byName;
          if (!match) return cat;

          return {
            ...cat,
            visibility: match.visibility ?? cat.visibility,
            input: match.input ?? cat.input,
            question: match.question ?? cat.question ?? '',
          };
        });

        // For any configured categories that did not match an existing org category, add them
        const matchedNames = new Set(merged.map((c: any) => (c.name || '').toLowerCase()));
        const toAdd = configuredCategories
          .filter((c: any) => !matchedNames.has((c.name || '').toLowerCase()))
          .map((c: any) => ({
            id: `temp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            name: c.name,
            visibility: c.visibility || 'private',
            input: c.input || 'ask_if_not_found',
            question: c.question || '',
            subtitle: c.name,
            priority: c.priority || 2,
            categories: c.options || [],
          }));

        const updated = [...merged, ...toAdd];

        await updateOrganization({ id: data.organizationId, patchedOrganization: { categories: updated } as any }).unwrap();

        if (toAdd.length > 0) addToast(`${toAdd.length} ${toAdd.length === 1 ? 'category' : 'categories'} added`, { appearance: 'success' });
        addToast('Category settings saved', { appearance: 'success' });
      } catch (err) {
        console.error('Failed to save configured categories:', err);
        addToast('Failed to save category settings', { appearance: 'error' });
      } finally {
        setIsSaving(false);
      }
    }

    onNext();
  };

  if (data.selectedCategories.length === 0) {
    return (
      <StepContainer>
        <Message warning>
          <Message.Header>No Categories Selected</Message.Header>
          <p>Please go back and select at least one category.</p>
        </Message>
        <ButtonContainer>
          <BackButton onClick={onBack}>Back to Categories</BackButton>
        </ButtonContainer>
      </StepContainer>
    );
  }

  return (
    <StepContainer>
      <StepHeader>
        <StepTitle>Configure Survey Categories</StepTitle>
        <StepSubtitle>
          Customize how each category will be used in surveys and member profiles.
        </StepSubtitle>
      </StepHeader>

      <Table>
        <TableHeader>
          <TableRow>
            <HeaderCell width={4}>Category</HeaderCell>
            <HeaderCell width={2}>Type</HeaderCell>
            <HeaderCell width={3}>Visibility</HeaderCell>
            <HeaderCell width={3}>Survey Prompt</HeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {configuredCategories.map((category, index) => (
            <TableRow key={category.id}>
              <TableCell $firstcol>
                <CategoryName>{category.name}</CategoryName>
                <CategoryMeta>{category.categories?.length || 0} options</CategoryMeta>
              </TableCell>
              
              <TableCell>
                <Label color={Number(category.priority) === 1 ? 'blue' : Number(category.priority) === 2 ? 'orange' : 'purple'}>
                  {Number(category.priority) === 1 ? 'Primary' : Number(category.priority) === 2 ? 'Custom' : 'Global'}
                </Label>
              </TableCell>

              <TableCell>
                <InlineSelect
                  compact
                  options={visibilityOptions}
                  value={category.visibility || 'private'}
                  onChange={(_: any, { value }: any) => handleFieldChange(index, 'visibility', value)}
                />
              </TableCell>

              <TableCell>
                <InlineSelect
                  compact
                  options={memberPromptOptions}
                  value={category.input || 'ask_if_not_found'}
                  onChange={(_: any, { value }: any) => handleFieldChange(index, 'input', value)}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <ButtonContainer>
        <LeftButtons>
          <BackButton onClick={onBack}>Back</BackButton>
          <CancelButton onClick={onCancel}>Cancel Setup</CancelButton>
        </LeftButtons>
        <NextButton onClick={handleContinue}>Continue</NextButton>
      </ButtonContainer>
    </StepContainer>
  );
};

export default OrganizationWizardStep3Demographics;

const StepContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
    padding-right: 32px;

`;

const StepHeader = styled.div`
  margin-bottom: 20px;
`;

const StepTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin: 0 0 4px 0;
`;

const StepSubtitle = styled.p`
  font-size: 13px;
  color: #666;
  margin: 0 0 16px 0;
  line-height: 1.4;
`;

const CategoryName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #333;
`;

const CategoryMeta = styled.div`
  font-size: 11px;
  color: #666;
  margin-top: 2px;
`;

const InlineSelect = styled(Select)`
  &&& {
    min-width: 150px;
    font-size: 13px;
  }
`;

const InlineInput = styled(Input as any)`
  &&& input {
    font-size: 13px;
    padding: 8px 10px;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #e0e0e0;
`;

const LeftButtons = styled.div`
  display: flex;
  gap: 12px;
`;

const BackButton = styled.button`
  padding: 10px 24px;
  background: transparent;
  border: 1px solid #d0d0d0;
  border-radius: 4px;
  color: #666;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: #f5f5f5;
    border-color: #b0b0b0;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CancelButton = styled.button`
  padding: 10px 24px;
  background: transparent;
  border: 1px solid #d0d0d0;
  border-radius: 4px;
  color: #666;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f5f5f5;
    border-color: #b0b0b0;
  }
`;

const NextButton = styled.button`
  padding: 10px 24px;
  background: #2185d0;
  border: none;
  border-radius: 4px;
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: #1678c2;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;
